import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { interviewAPI } from '../services/api';

function InterviewLeaderboard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [leaderboardData, setLeaderboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedStudent, setExpandedStudent] = useState(null);

  useEffect(() => {
    fetchLeaderboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchLeaderboard = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await interviewAPI.getLeaderboard(id);
      setLeaderboardData(response.data);
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
      setError(error.response?.data?.message || 'Failed to load leaderboard');
    }
    setLoading(false);
  };

  const getRankBadgeColor = (rank) => {
    if (rank === 1) return { bg: '#ffd700', color: '#8b6914', icon: '🥇' };
    if (rank === 2) return { bg: '#c0c0c0', color: '#4a4a4a', icon: '🥈' };
    if (rank === 3) return { bg: '#cd7f32', color: '#664016', icon: '🥉' };
    return { bg: '#e2e8f0', color: '#4a5568', icon: '🏅' };
  };

  const formatTimeSpent = (milliseconds) => {
    const minutes = Math.floor(milliseconds / 60000);
    const seconds = Math.floor((milliseconds % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
  };

  const toggleStudentExpansion = (studentId) => {
    setExpandedStudent(expandedStudent === studentId ? null : studentId);
  };

  if (loading) {
    return (
      <div className="container">
        <div className="card">
          <div className="loading">Loading leaderboard...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container">
        <div className="card">
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <p style={{ color: '#e53e3e', marginBottom: '20px' }}>{error}</p>
            <button onClick={() => navigate(-1)} className="btn btn-secondary">
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="card">
        {/* Header */}
        <div style={{ marginBottom: '32px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <div>
              <h2 style={{ color: '#667eea', fontSize: '26px', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                🏆 Interview Leaderboard
              </h2>
              <h3 style={{ color: '#2d3748', fontSize: '18px', margin: '8px 0' }}>
                {leaderboardData?.interview?.title}
              </h3>
              {leaderboardData?.interview?.description && (
                <p style={{ color: '#718096', fontSize: '14px', margin: '4px 0' }}>
                  {leaderboardData.interview.description}
                </p>
              )}
            </div>
            <button onClick={() => navigate(-1)} className="btn btn-secondary">
              ← Back to Interviews
            </button>
          </div>

          {/* Stats summary */}
          <div style={{ display: 'flex', gap: '20px', padding: '20px', background: '#f7fafc', borderRadius: '12px', marginTop: '20px' }}>
            <div>
              <span style={{ fontSize: '12px', color: '#718096', display: 'block', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Total Participants
              </span>
              <span style={{ fontSize: '28px', fontWeight: 700, color: '#667eea' }}>
                {leaderboardData?.totalParticipants || 0}
              </span>
            </div>
            <div>
              <span style={{ fontSize: '12px', color: '#718096', display: 'block', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Total Questions
              </span>
              <span style={{ fontSize: '28px', fontWeight: 700, color: '#2d3748' }}>
                {leaderboardData?.interview?.totalQuestions || 0}
              </span>
            </div>
          </div>
        </div>

        {/* Leaderboard Table */}
        {leaderboardData?.leaderboard && leaderboardData.leaderboard.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ 
              width: '100%', 
              borderCollapse: 'collapse',
              fontSize: '14px'
            }}>
              <thead>
                <tr style={{ 
                  background: '#f7fafc', 
                  borderBottom: '2px solid #e2e8f0'
                }}>
                  <th style={{ padding: '16px 12px', textAlign: 'left', fontWeight: 600, color: '#4a5568' }}>
                    Rank
                  </th>
                  <th style={{ padding: '16px 12px', textAlign: 'left', fontWeight: 600, color: '#4a5568' }}>
                    Student
                  </th>
                  <th style={{ padding: '16px 12px', textAlign: 'center', fontWeight: 600, color: '#4a5568' }}>
                    Total Attempts
                  </th>
                  <th style={{ padding: '16px 12px', textAlign: 'center', fontWeight: 600, color: '#4a5568' }}>
                    Best Score
                  </th>
                  <th style={{ padding: '16px 12px', textAlign: 'center', fontWeight: 600, color: '#4a5568' }}>
                    Average Score
                  </th>
                  <th style={{ padding: '16px 12px', textAlign: 'center', fontWeight: 600, color: '#4a5568' }}>
                    Details
                  </th>
                </tr>
              </thead>
              <tbody>
                {leaderboardData.leaderboard.map((entry, index) => {
                  const rankStyle = getRankBadgeColor(entry.rank);
                  const isExpanded = expandedStudent === entry.student.id;
                  
                  return (
                    <React.Fragment key={entry.student.id}>
                      <tr style={{ 
                        borderBottom: '1px solid #e2e8f0',
                        background: index % 2 === 0 ? 'white' : '#fafafa',
                        transition: 'background 0.2s'
                      }}>
                        <td style={{ padding: '16px 12px' }}>
                          <div style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '6px 12px',
                            borderRadius: '20px',
                            background: rankStyle.bg,
                            color: rankStyle.color,
                            fontWeight: 600,
                            fontSize: '14px'
                          }}>
                            <span>{rankStyle.icon}</span>
                            <span>#{entry.rank}</span>
                          </div>
                        </td>
                        <td style={{ padding: '16px 12px' }}>
                          <div>
                            <div style={{ fontWeight: 600, color: '#2d3748', marginBottom: '2px' }}>
                              {entry.student.name}
                            </div>
                            <div style={{ fontSize: '12px', color: '#718096' }}>
                              {entry.student.email}
                            </div>
                          </div>
                        </td>
                        <td style={{ padding: '16px 12px', textAlign: 'center' }}>
                          <span style={{
                            padding: '4px 12px',
                            borderRadius: '12px',
                            background: '#e9d8fd',
                            color: '#6b46c1',
                            fontWeight: 600
                          }}>
                            {entry.totalAttempts}
                          </span>
                        </td>
                        <td style={{ padding: '16px 12px', textAlign: 'center' }}>
                          <div style={{
                            fontSize: '18px',
                            fontWeight: 700,
                            color: entry.bestScore >= 80 ? '#38a169' : entry.bestScore >= 60 ? '#f59e0b' : '#e53e3e'
                          }}>
                            {entry.bestScore.toFixed(1)}%
                          </div>
                        </td>
                        <td style={{ padding: '16px 12px', textAlign: 'center' }}>
                          <div style={{
                            fontSize: '16px',
                            fontWeight: 600,
                            color: '#4a5568'
                          }}>
                            {entry.averageScore.toFixed(1)}%
                          </div>
                        </td>
                        <td style={{ padding: '16px 12px', textAlign: 'center' }}>
                          <button
                            onClick={() => toggleStudentExpansion(entry.student.id)}
                            className="btn btn-secondary"
                            style={{ padding: '6px 16px', fontSize: '13px' }}
                          >
                            {isExpanded ? 'Hide' : 'Show'} Attempts
                          </button>
                        </td>
                      </tr>
                      
                      {/* Expanded row showing all attempts */}
                      {isExpanded && (
                        <tr style={{ background: '#f7fafc' }}>
                          <td colSpan="6" style={{ padding: '20px' }}>
                            <div style={{ 
                              background: 'white', 
                              borderRadius: '8px', 
                              padding: '16px',
                              border: '1px solid #e2e8f0'
                            }}>
                              <h4 style={{ 
                                margin: '0 0 16px 0', 
                                color: '#2d3748', 
                                fontSize: '16px',
                                fontWeight: 600 
                              }}>
                                All Attempts for {entry.student.name}
                              </h4>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                {entry.attempts.map((attempt) => (
                                  <div
                                    key={attempt.attemptNumber}
                                    style={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                      padding: '12px 16px',
                                      background: '#fafafa',
                                      borderRadius: '6px',
                                      border: '1px solid #e2e8f0'
                                    }}
                                  >
                                    <div style={{ display: 'flex', gap: '24px', alignItems: 'center' }}>
                                      <div>
                                        <span style={{ 
                                          fontSize: '12px', 
                                          color: '#718096', 
                                          display: 'block',
                                          marginBottom: '4px'
                                        }}>
                                          Attempt
                                        </span>
                                        <span style={{ 
                                          fontSize: '20px', 
                                          fontWeight: 700, 
                                          color: '#667eea' 
                                        }}>
                                          #{attempt.attemptNumber}
                                        </span>
                                      </div>
                                      <div>
                                        <span style={{ 
                                          fontSize: '12px', 
                                          color: '#718096', 
                                          display: 'block',
                                          marginBottom: '4px'
                                        }}>
                                          Score
                                        </span>
                                        <span style={{ 
                                          fontSize: '20px', 
                                          fontWeight: 700,
                                          color: attempt.score >= 80 ? '#38a169' : attempt.score >= 60 ? '#f59e0b' : '#e53e3e'
                                        }}>
                                          {attempt.score.toFixed(1)}%
                                        </span>
                                      </div>
                                      <div>
                                        <span style={{ 
                                          fontSize: '12px', 
                                          color: '#718096', 
                                          display: 'block',
                                          marginBottom: '4px'
                                        }}>
                                          Questions Answered
                                        </span>
                                        <span style={{ 
                                          fontSize: '16px', 
                                          fontWeight: 600, 
                                          color: '#2d3748' 
                                        }}>
                                          {attempt.totalQuestions}
                                        </span>
                                      </div>
                                      {attempt.timeSpent > 0 && (
                                        <div>
                                          <span style={{ 
                                            fontSize: '12px', 
                                            color: '#718096', 
                                            display: 'block',
                                            marginBottom: '4px'
                                          }}>
                                            Time Spent
                                          </span>
                                          <span style={{ 
                                            fontSize: '16px', 
                                            fontWeight: 600, 
                                            color: '#2d3748' 
                                          }}>
                                            {formatTimeSpent(attempt.timeSpent)}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                    <div style={{ textAlign: 'right' }}>
                                      <span style={{ 
                                        fontSize: '12px', 
                                        color: '#718096', 
                                        display: 'block',
                                        marginBottom: '4px'
                                      }}>
                                        Submitted
                                      </span>
                                      <span style={{ 
                                        fontSize: '13px', 
                                        color: '#4a5568' 
                                      }}>
                                        {new Date(attempt.submittedAt).toLocaleString()}
                                      </span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📊</div>
            <p style={{ color: '#718096', fontSize: '16px', marginBottom: '8px' }}>
              No submissions yet
            </p>
            <p style={{ color: '#a0aec0', fontSize: '14px' }}>
              The leaderboard will appear once students submit their interviews.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default InterviewLeaderboard;
